import {useMemo} from 'react';
import {FieldErrors, FieldValues, Path, UseFormWatch} from 'react-hook-form';

/**
 * Hook that returns memoized field error props for react-hook-form.
 * Handles serialization of errors object to detect changes when
 * the object reference doesn't change (react-hook-form may mutate in place).
 *
 * @param isSubmitted - Whether the form has been submitted
 * @param watch - The watch function from react-hook-form
 * @param errors - The errors object from react-hook-form
 * @returns Memoized error props object with error and helperText for each field
 */
export const useFieldErrorProps = <T extends FieldValues>(
    isSubmitted: boolean,
    watch: UseFormWatch<T>,
    errors: FieldErrors<T>,
) => {
    const errorsKey = JSON.stringify(
        Object.keys(errors).reduce(
            (acc, key) => {
                const error = errors[key as keyof typeof errors];
                if (error) {
                    acc[key] = {
                        type: String(error.type ?? ''),
                        message: String(error.message ?? ''),
                    };
                }
                return acc;
            },
            {} as Record<string, {type: string; message?: string}>,
        ),
    );

    return useMemo(
        () => getFieldErrorProps<T>(isSubmitted, watch, errors),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isSubmitted, watch, errorsKey],
    );
};

const getFieldErrorProps = <T extends FieldValues>(
    isSubmitted: boolean,
    watch: UseFormWatch<T>,
    errors: FieldErrors<T>,
) => {
    return (Object.keys(errors) as (keyof T)[]).reduce(
        (acc, field) => {
            const value = watch(field as Path<T>);
            const hasError = Boolean((isSubmitted || value) && errors[field]);

            acc[field] = {
                error: hasError,
                helperText: hasError
                    ? typeof errors[field]?.message === 'string'
                        ? (errors[field]?.message as string)
                        : ''
                    : '',
            };
            return acc;
        },
        {} as Record<keyof T, {error: boolean; helperText: string}>,
    );
};
